import {useEffect, useRef} from 'react';
import {useQuery, useQueryClient, type QueryKey} from '@tanstack/react-query';
import {api} from '../api';

const terminalStates = new Set(['succeeded', 'failed_retryable', 'failed_terminal']);

export const useWorkOperation = (operationId: string | null, invalidate: readonly QueryKey[]) => {
  const queryClient = useQueryClient();
  const handledTerminal = useRef<string | null>(null);
  const operation = useQuery({
    queryKey: ['operation', operationId],
    queryFn: () => api.operation(operationId!),
    enabled: Boolean(operationId),
    retry: 2,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state && terminalStates.has(state)) return false;
      const attempt = Math.min(query.state.dataUpdateCount, 3);
      return 1_000 * (2 ** attempt);
    },
  });

  useEffect(() => {
    if (!operationId || !operation.data || !terminalStates.has(operation.data.state)) return;
    if (handledTerminal.current === operationId) return;
    handledTerminal.current = operationId;
    void Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({queryKey})));
  }, [invalidate, operation.data, operationId, queryClient]);

  return operation;
};
